import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (conversationId: string) => void;
}

const NewChatDialog = ({ open, onOpenChange, onCreated }: Props) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const start = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setLoading(true);
    try {
      const { data: u, error: lookupErr } = await supabase
        .rpc("lookup_chat_user_by_email", { _email: trimmed })
        .maybeSingle();
      if (lookupErr) throw lookupErr;
      if (!u?.user_id) {
        toast.error("No user found with that email");
        return;
      }
      const { data: convId, error } = await supabase.rpc("get_or_create_direct_conversation", {
        _other_user: u.user_id,
      });
      if (error) throw error;
      onOpenChange(false);
      setEmail("");
      onCreated(convId as string);
    } catch (e) {
      const msg = (e as Error).message || "";
      if (msg.includes("Messaging is not available")) {
        toast.error("Messaging is not available with this user.");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start a new chat</DialogTitle>
          <DialogDescription>
            Enter the email of the person you'd like to message. They must have a GetPetVet account.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            type="email"
            placeholder="name@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && start()}
            autoFocus
          />
          <Button onClick={start} disabled={loading || !email.trim()} className="w-full">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Start chat
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewChatDialog;
